import { Transaction } from '../hooks/useTransactionData';

// Cores para os gráficos
export const CHART_COLORS = {
  primary: '#26372D',
  secondary: '#4CAF50',
  accent: '#FF9800',
  danger: '#F44336',
  warning: '#FFC107',
  info: '#2196F3',
  success: '#8BC34A',
  purple: '#9C27B0',
  pink: '#E91E63',
  teal: '#009688',
  indigo: '#3F51B5',
  cyan: '#00BCD4',
  lime: '#CDDC39',
  amber: '#FFC107',
  deepOrange: '#FF5722',
  brown: '#795548',
  grey: '#9E9E9E',
  blueGrey: '#607D8B',
  // Cores mais vibrantes para gráficos
  vibrantGreen: '#4CAF50',
  vibrantRed: '#F44336',
  vibrantBlue: '#2196F3',
  vibrantOrange: '#FF9800',
  vibrantPurple: '#9C27B0',
  vibrantTeal: '#009688',
  vibrantPink: '#E91E63',
  vibrantIndigo: '#3F51B5',
  vibrantCyan: '#00BCD4',
  vibrantLime: '#8BC34A',
};

export const COLOR_PALETTE = [
  CHART_COLORS.vibrantGreen,
  CHART_COLORS.vibrantBlue,
  CHART_COLORS.vibrantOrange,
  CHART_COLORS.vibrantPurple,
  CHART_COLORS.vibrantTeal,
  CHART_COLORS.vibrantPink,
  CHART_COLORS.vibrantIndigo,
  CHART_COLORS.vibrantCyan,
  CHART_COLORS.vibrantLime,
  CHART_COLORS.primary,
  CHART_COLORS.amber,
  CHART_COLORS.deepOrange,
  CHART_COLORS.brown,
  CHART_COLORS.grey,
  CHART_COLORS.blueGrey,
];

// Função para gerar cores automaticamente
export const generateColors = (count: number): string[] => {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    colors.push(COLOR_PALETTE[i % COLOR_PALETTE.length]);
  }
  return colors;
};

// Processar dados para gráfico de pizza por categoria
export const processDataByCategory = (transactions: Transaction[], type?: 'Entrada' | 'Saída') => {
  const filtered = type ? transactions.filter(t => t.tctName === type) : transactions;
  
  const categoryData = filtered.reduce((acc, transaction) => {
    const category = transaction.tccName;
    acc[category] = (acc[category] || 0) + transaction.tscValue;
    return acc;
  }, {} as Record<string, number>);

  const labels = Object.keys(categoryData);
  const data = Object.values(categoryData);
  const colors = generateColors(labels.length);

  return {
    labels,
    datasets: [{
      data,
      backgroundColor: colors,
      borderColor: ['#ffffff'],
      borderWidth: 3,
      hoverBackgroundColor: colors.map(color => color + 'CC'),
      hoverBorderWidth: 4,
    }]
  };
};

// Processar dados para gráfico de barras por usuário
export const processDataByUser = (transactions: Transaction[]) => {
  const userData = transactions.reduce((acc, transaction) => {
    const user = transaction.usrName;
    if (!acc[user]) {
      acc[user] = { entrada: 0, saida: 0 };
    }
    
    if (transaction.tctName === 'Entrada') {
      acc[user].entrada += transaction.tscValue;
    } else {
      acc[user].saida += transaction.tscValue;
    }
    
    return acc;
  }, {} as Record<string, { entrada: number; saida: number }>);

  const labels = Object.keys(userData);
  const entradaData = labels.map(user => userData[user].entrada);
  const saidaData = labels.map(user => userData[user].saida);

  return {
    labels,
    datasets: [
      {
        label: 'Entradas',
        data: entradaData,
        backgroundColor: CHART_COLORS.vibrantGreen,
        borderColor: CHART_COLORS.vibrantGreen,
        borderWidth: 2,
        borderRadius: 4,
        hoverBackgroundColor: CHART_COLORS.vibrantGreen + 'CC',
      },
      {
        label: 'Saídas',
        data: saidaData,
        backgroundColor: CHART_COLORS.vibrantRed,
        borderColor: CHART_COLORS.vibrantRed,
        borderWidth: 2,
        borderRadius: 4,
        hoverBackgroundColor: CHART_COLORS.vibrantRed + 'CC',
      }
    ]
  };
};

// Processar dados para gráfico de status de pagamento
export const processDataByPaymentStatus = (transactions: Transaction[]) => {
  const statusData = transactions.reduce((acc, transaction) => {
    const status = transaction.tscCheck === 1 ? 'Pago' : 'Pendente';
    acc[status] = (acc[status] || 0) + transaction.tscValue;
    return acc;
  }, {} as Record<string, number>);

  const labels = Object.keys(statusData);
  const data = Object.values(statusData);

  return {
    labels,
    datasets: [{
      data,
      backgroundColor: [CHART_COLORS.vibrantGreen, CHART_COLORS.warning],
      borderColor: ['#ffffff', '#ffffff'],
      borderWidth: 3,
      hoverBackgroundColor: [CHART_COLORS.vibrantGreen + 'CC', CHART_COLORS.warning + 'CC'],
      hoverBorderWidth: 4,
    }]
  };
};

// Processar dados para comparação Entrada vs Saída
export const processIncomeVsExpense = (transactions: Transaction[]) => {
  const totals = transactions.reduce((acc, transaction) => {
    if (transaction.tctName === 'Entrada') {
      acc.entrada += transaction.tscValue;
    } else {
      acc.saida += transaction.tscValue;
    }
    return acc;
  }, { entrada: 0, saida: 0 });

  return {
    labels: ['Entradas', 'Saídas'],
    datasets: [{
      data: [totals.entrada, totals.saida],
      backgroundColor: [CHART_COLORS.vibrantGreen, CHART_COLORS.vibrantRed],
      borderColor: ['#ffffff', '#ffffff'],
      borderWidth: 3,
      hoverBackgroundColor: [CHART_COLORS.vibrantGreen + 'CC', CHART_COLORS.vibrantRed + 'CC'],
      hoverBorderWidth: 4,
    }]
  };
};

// Processar dados para top categorias de gastos
export const processTopExpenseCategories = (transactions: Transaction[], limit: number = 5) => {
  const expenses = transactions.filter(t => t.tctName === 'Saída');
  const categoryData = expenses.reduce((acc, transaction) => {
    const category = transaction.tccName;
    acc[category] = (acc[category] || 0) + transaction.tscValue;
    return acc;
  }, {} as Record<string, number>);

  const sortedCategories = Object.entries(categoryData)
    .sort(([,a], [,b]) => b - a)
    .slice(0, limit);

  const labels = sortedCategories.map(([category]) => category);
  const data = sortedCategories.map(([, value]) => value);
  const colors = generateColors(labels.length);

  return {
    labels,
    datasets: [{
      label: 'Gastos por Categoria',
      data,
      backgroundColor: colors,
      borderColor: colors,
      borderWidth: 2,
      borderRadius: 6,
      hoverBackgroundColor: colors.map(color => color + 'CC'),
      hoverBorderWidth: 3,
    }]
  };
};

// Calcular estatísticas resumidas
export const calculateSummaryStats = (transactions: Transaction[]) => {
  const stats = transactions.reduce((acc, transaction) => {
    if (transaction.tctName === 'Entrada') {
      acc.totalEntrada += transaction.tscValue;
      acc.countEntrada++;
    } else {
      acc.totalSaida += transaction.tscValue;
      acc.countSaida++;
    }
    
    if (transaction.tscCheck === 1) {
      acc.totalPago += transaction.tscValue;
      acc.countPago++;
    } else {
      acc.totalPendente += transaction.tscValue;
      acc.countPendente++;
    }
    
    return acc;
  }, {
    totalEntrada: 0,
    totalSaida: 0,
    countEntrada: 0,
    countSaida: 0,
    totalPago: 0,
    totalPendente: 0,
    countPago: 0,
    countPendente: 0,
  });

  return {
    ...stats,
    saldo: stats.totalEntrada - stats.totalSaida,
    totalTransacoes: transactions.length,
    ticketMedioEntrada: stats.countEntrada > 0 ? stats.totalEntrada / stats.countEntrada : 0,
    ticketMedioSaida: stats.countSaida > 0 ? stats.totalSaida / stats.countSaida : 0,
  };
};